import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { useLanguage } from '../../i18n/LanguageContext';
import MailboxReviewForm from './MailboxReviewForm';
import { operationalNoticeTitles } from '../distributor/DistributorOperationalNotices';

const idPattern = /^[a-f\d]{24}$/i;
const date = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const statuses = {
  pending: 'Queued',
  sending: 'Sending — outcome unconfirmed',
  needs_review: 'Needs mailbox review',
};
const roles = {
  admin: 'MedEffects admin',
  master_distributor: 'Distributor user',
  unavailable: 'Account unavailable',
};
const blank = {
  noticeId: '',
  userId: '',
  expectedRevision: 0,
  outcome: 'sent',
  messageId: '',
  reason: '',
  confirmedMailbox: false,
};
const key = (row) => `${row.noticeId}:${row.userId}`;
const isAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem('user'))?.role === 'admin';
  } catch {
    return false;
  }
};

export default function OperationalNoticeDeliveryQueue() {
  const { t, locale } = useLanguage();
  const [queue, setQueue] = useState(null),
    [page, setPage] = useState(1),
    [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [review, setReview] = useState(blank),
    [uncertain, setUncertain] = useState(false);
  const busyRef = useRef(false);
  const { begin, version } = useShippingRequest('operational-notice-deliveries');
  const load = useCallback(
    async (nextPage = 1) => {
      if (busyRef.current) return;
      const request = begin();
      setQueue(null);
      setReview(blank);
      setLoading(true);
      setError('');
      if (!isAdmin()) {
        setLoading(false);
        return;
      }
      try {
        const value = (
          await distributorService.adminOperationalNoticeDeliveries(nextPage, {
            signal: request.signal,
          })
        )?.data?.data;
        if (!request.isCurrent()) return;
        if (
          !Array.isArray(value?.items) ||
          value.items.length > 20 ||
          value.page !== Math.min(nextPage, Math.max(1, value.totalPages)) ||
          !Number.isSafeInteger(value.total) ||
          value.total < 0 ||
          value.totalPages !== Math.ceil(value.total / 20) ||
          typeof value.emailEnabled !== 'boolean' ||
          typeof value.discoveryEnabled !== 'boolean' ||
          new Set(value.items.map(key)).size !== value.items.length ||
          value.items.some(
            (row) =>
              !idPattern.test(row?.noticeId) ||
              !idPattern.test(row.userId) ||
              !idPattern.test(row.distributorId) ||
              typeof row.recipientName !== 'string' ||
              !Object.hasOwn(roles, row.recipientRole) ||
              !Object.hasOwn(statuses, row.status) ||
              !Object.hasOwn(operationalNoticeTitles, row.kind) ||
              !Number.isSafeInteger(row.revision) ||
              row.revision < 0 ||
              typeof row.canReview !== 'boolean' ||
              !(row.claimedAt === null || date(row.claimedAt)) ||
              (row.canReview && (row.status === 'pending' || !date(row.claimedAt)))
          )
        )
          throw new Error('Invalid operational email queue');
        setQueue(value);
        setPage(value.page);
        setUncertain(false);
      } catch {
        if (request.isCurrent())
          setError('Operational notice email recovery is unavailable. Please retry.');
      } finally {
        if (request.isCurrent()) setLoading(false);
      }
    },
    [begin]
  );
  useEffect(() => {
    busyRef.current = false;
    setBusy(false);
    setUncertain(false);
    setNotice('');
    load(1);
  }, [load, version]);
  const reconcile = async (event) => {
    event.preventDefault();
    const captured = { ...review },
      row = queue?.items.find((item) => key(item) === key(captured));
    if (
      !isAdmin() ||
      busyRef.current ||
      loading ||
      uncertain ||
      !row?.canReview ||
      row.revision !== captured.expectedRevision ||
      !captured.confirmedMailbox ||
      captured.reason.trim().length < 12 ||
      captured.reason.length > 300 ||
      !['sent', 'not_sent'].includes(captured.outcome) ||
      (captured.outcome === 'sent' &&
        !/^(?:[A-Za-z0-9._:@-]{6,200}|<[A-Za-z0-9._:@-]{4,198}>)$/.test(captured.messageId))
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const request = begin();
    try {
      const receipt = (
        await distributorService.reconcileOperationalNoticeDelivery(
          captured.noticeId,
          captured.userId,
          {
            expectedRevision: captured.expectedRevision,
            outcome: captured.outcome,
            confirmedMailbox: true,
            reason: captured.reason,
            ...(captured.outcome === 'sent' ? { messageId: captured.messageId } : {}),
          }
        )
      )?.data?.data;
      if (!request.isCurrent()) return;
      if (
        receipt?.noticeId !== captured.noticeId ||
        receipt.userId !== captured.userId ||
        receipt.revision !== captured.expectedRevision + 1 ||
        receipt.status !== (captured.outcome === 'sent' ? 'sent' : 'pending')
      )
        throw new Error('Invalid operational review receipt');
      setNotice(
        captured.outcome === 'sent'
          ? 'Mailbox review saved. This review did not send another alert.'
          : 'Mailbox review saved. The notice is queued and can send when enabled and its recipient remains eligible.'
      );
      busyRef.current = false;
      setBusy(false);
      await load(page);
    } catch {
      if (request.isCurrent()) {
        setQueue(null);
        setUncertain(true);
        setError(
          'Operational notice review was not confirmed. Refresh and check the mailbox before another decision.'
        );
      }
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  if (!isAdmin()) return null;
  return (
    <section
      className="me-distributor-admin-card me-distributor-admin-queue space-y-4"
      aria-label={t('Operational notice email recovery')}
    >
      <h2 className="text-lg font-semibold">{t('Operational notice email recovery')}</h2>
      {queue && (
        <p role="status">
          {t(
            queue.emailEnabled
              ? 'Operational notice emails are enabled.'
              : 'Operational notice emails are off. In-app notices remain available.'
          )}
        </p>
      )}
      {queue && !queue.discoveryEnabled && (
        <p role="status">
          {t('Automatic notice checks are off. Existing notices remain available.')}
        </p>
      )}
      <button type="button" disabled={busy || loading} onClick={() => load(page)}>
        {t('Refresh operational email queue')}
      </button>
      {loading && <p role="status">{t('Loading operational emails...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {queue?.items.length === 0 && <p>{t('No operational emails need recovery.')}</p>}
      {queue?.items.map((row) => (
        <article className="me-distributor-admin-queue-row space-y-2" key={key(row)}>
          <h3 className="font-semibold">{t(operationalNoticeTitles[row.kind])}</h3>
          <p>
            {t('Distributor reference')}: {row.distributorId}
          </p>
          <p>
            {row.recipientName} · {t(roles[row.recipientRole])} · {t(statuses[row.status])}
          </p>
          {row.claimedAt && (
            <p>
              {t('Email attempt')}: {new Date(row.claimedAt).toLocaleString(locale)}
            </p>
          )}
          {row.status === 'pending' && (
            <p>{t('Queued for the existing sender. No manual resend is needed.')}</p>
          )}
          {row.status !== 'pending' && !row.canReview && (
            <p>
              {t(
                'An active or incomplete attempt cannot be reviewed yet. Wait at least 15 minutes, then refresh.'
              )}
            </p>
          )}
          {row.canReview && (
            <button
              type="button"
              disabled={busy || loading}
              onClick={() =>
                setReview({
                  ...blank,
                  noticeId: row.noticeId,
                  userId: row.userId,
                  expectedRevision: row.revision,
                })
              }
            >
              {t('Review operational email evidence')}
            </button>
          )}
          {key(review) === key(row) && (
            <>
              {review.outcome === 'not_sent' && (
                <p>
                  {t(
                    'Confirmed not sent requeues this notice. It may send automatically after review if enabled.'
                  )}
                </p>
              )}
              <MailboxReviewForm
                review={review}
                setReview={setReview}
                disabled={busy || loading || uncertain}
                onSubmit={reconcile}
                onCancel={() => setReview(blank)}
              />
            </>
          )}
        </article>
      ))}
      {queue?.totalPages > 1 && (
        <nav aria-label={t('Operational email pages')} className="flex gap-3">
          <button
            type="button"
            disabled={busy || loading || page <= 1}
            onClick={() => load(page - 1)}
          >
            {t('Previous')}
          </button>
          <span>{t('Page {page} of {pages}', { page, pages: queue.totalPages })}</span>
          <button
            type="button"
            disabled={busy || loading || page >= queue.totalPages}
            onClick={() => load(page + 1)}
          >
            {t('Next')}
          </button>
        </nav>
      )}
    </section>
  );
}
